import React, { useState, useMemo } from "react";
import Grid, { GridItem } from "@/components/layout/Grid";
import { FormCamposProps } from "./FormCamposProps";
import CampoTexto from "@/components/Formulario/CampoTexto";
import CampoSenha from "@/components/Formulario/CampoSenha";
import Botao from "@/components/Botao";
import HBox from "@/components/layout/HBox";
import VBox from "@/components/layout/VBox";
import Icon from "@/components/Icon";
import {
    mdiCircle,
    mdiChevronDown,
    mdiChevronUp,
    mdiLoading,
    mdiRefresh,
} from "@mdi/js";
import { toast } from "react-toastify";
import axios from "axios";
import { useQuery } from "react-query";
import { CanalChaveApiVoip, PhoneNumberFormat } from "@tera/shared/src/canalCodecs/CanalChaveApiCodecVoip";
import CampoMultiTexto from "@/components/Formulario/CampoMultiTexto";
import CampoMemo from "@/components/Formulario/CampoMemo";
import CampoSeletor from "@/components/Formulario/CampoSeletor";

type SipStatus = {
    online: boolean;
    lastSeen?: string;
};

export const FormCamposVoip = ({
    canalId,
    host,
    value,
    onChange,
    fieldErrors,
    props,
    onChangeProps,
}: FormCamposProps) => {
    // Parse do valor JSON
    const data: Partial<CanalChaveApiVoip> = value
        ? (value.startsWith('{') ? JSON.parse(value) : { tipo: "voip" })
        : { tipo: "voip" };

    const isEditMode = !!canalId && canalId > 0;

    // Query para verificar status do trunk SIP
    const { data: sipStatus, isLoading: statusLoading, refetch: refetchStatus } = useQuery<SipStatus>(
        ["canal-sip-status", canalId],
        async () => {
            if (!canalId || !isEditMode) return { online: false };
            const res = await axios.get(`/api/canal/${canalId}/sip-status`);
            // Backend retorna { result: { connected: boolean, ... } }
            const result = res.data?.result || res.data;
            return { online: result.connected ?? false };
        },
        {
            enabled: isEditMode && !!data.sipTrunkId,
            refetchInterval: 30000, // Atualiza a cada 30s
            retry: false,
        }
    );

    // Query para buscar grupos/departamentos
    const queryGrupos = useQuery(
        ['usuario_grupo'],
        async () => {
            const res = await fetch('/api/usuario_grupo/box');
            return res.json();
        },
    );

    const gruposOptions = useMemo(() => {
        return queryGrupos.data?.result?.map((it: any) => ({
            id: it.id,
            label: it.nome,
        })) ?? [];
    }, [queryGrupos.data]);

    const updateData = (updates: Partial<CanalChaveApiVoip>) => {
        const newData = { ...data, ...updates, tipo: "voip" as const };
        onChange?.(JSON.stringify(newData));
    };

    const isValidFormat = (number: string, format: PhoneNumberFormat) => {
        if (format === 'E164') {
            return /^\+\d{11,16}$/.test(number);
        } else if (format === 'N10') {
            return /^\d{10,11}$/.test(number);
        }
        return false;
    }

    const isDidInboundValid = data?.did && isValidFormat(data.did || '', data.phoneNumberFormat || 'E164')
    const isCallerIdOutboundValid = data?.outboundFromNumber ? isValidFormat(data.outboundFromNumber || '', data.phoneNumberFormat || 'E164') : true;

    return (
        <VBox gap="16px">
            {/* Erro geral */}
            {fieldErrors?.["chave_api"] && (
                <div style={{ color: "var(--tc-color-danger)" }}>
                    {fieldErrors["chave_api"]}
                </div>
            )}

            <Grid>
                <GridItem sm={12}>
                    <CampoSeletor
                        label="Formato do Número"
                        value={data.phoneNumberFormat || 'E164'}
                        options={[
                            { id: 'E164', label: 'E.164 (ex: +5562911112222)' },
                            { id: 'N10', label: 'N10 (ex: 6211112222)' },
                        ]}
                        onChange={(v) => updateData({ phoneNumberFormat: v as PhoneNumberFormat })}
                    />
                    <small style={{ color: "var(--tc-color-gray-500)" }}>
                        Em dúvida, verifique com seu provedor VoIP qual é o formato correto.
                    </small>
                </GridItem>
            </Grid>

            {/* Seção: Propriedades do atendimento por voz */}
            <div
                style={{
                    border: "1px solid var(--tc-color-gray-300)",
                    borderRadius: "8px",
                    padding: "16px",
                }}
            >
                <h4 style={{ margin: "0 0 16px 0" }}>Propriedades do atendimento por voz</h4>

                <Grid>
                    <GridItem sm={12} md={4}>
                        <CampoMemo
                            label="Mensagem de boas-vindas"
                            name="mensagem_boas_vindas"
                            value={props?.mensagemBoasVindas || ''}
                            onChange={(v) => {
                                onChangeProps?.({
                                    ...(props ?? {}),
                                    mensagemBoasVindas: v,
                                });
                            }}
                        />
                    </GridItem>

                    <GridItem sm={12} md={4}>
                        <CampoSeletor
                            label="Grupo / departamento responsável"
                            name="grupo_responsavel_id"
                            value={props?.grupo_responsavel_id}
                            options={gruposOptions}
                            onChange={(v) => {
                                onChangeProps?.({
                                    ...(props ?? {}),
                                    grupo_responsavel_id: v,
                                });
                            }}
                        />
                    </GridItem>

                    <GridItem sm={12} md={4}>
                        <CampoMemo
                            label="Mensagem de despedida"
                            name="mensagem_despedida"
                            value={props?.mensagemDespedida || ''}
                            onChange={(v) => {
                                onChangeProps?.({
                                    ...(props ?? {}),
                                    mensagemDespedida: v,
                                });
                            }}
                        />
                    </GridItem>
                </Grid>
            </div>


            <Grid>
                <GridItem sm={12} md={6}>
                    {/* Seção: Configuração de Recebimento (Inbound) */}
                    <div
                        style={{
                            border: "1px solid var(--tc-color-gray-300)",
                            borderRadius: "8px",
                            padding: "12px 16px",
                        }}
                    >
                        <HBox style={{ justifyContent: "space-between", marginBottom: "16px" }}>
                            <h4 style={{ margin: 0 }}>Recebimento de Chamadas (Inbound)</h4>
                            {isEditMode && data.sipTrunkId && (
                                <HBox gap="8px" style={{ alignItems: "center", flex: '1 0 0' }}>
                                    {statusLoading ? (
                                        <Icon path={mdiLoading} size={0.6} spin color="var(--tc-color-gray-500)" />
                                    ) : (
                                        <>
                                            <Icon
                                                path={mdiCircle}
                                                size={0.5}
                                                color={sipStatus?.online ? "var(--tc-color-success)" : "var(--tc-color-danger)"}
                                            />
                                            <span style={{ fontSize: "14px", color: "var(--tc-color-gray-600)" }}>
                                                {sipStatus?.online ? "Online" : "Offline"}
                                            </span>
                                        </>
                                    )}
                                    <Botao
                                        variant="none-secondary"
                                        onClick={() => refetchStatus()}
                                        title="Atualizar status"
                                    >
                                        <Icon path={mdiRefresh} size={0.7} />
                                    </Botao>
                                </HBox>
                            )}
                        </HBox>

                        <p style={{ marginBottom: "16px", color: "var(--tc-color-gray-600)" }}>
                            Configure o número DID que será associado a este canal.
                            Chamadas recebidas para este número serão direcionadas para os atendentes.
                        </p>

                        <Grid>
                            <GridItem sm={12} md={6}>
                                <CampoTexto
                                    label="Número DID *"
                                    placeholder="Ex: 6230301012 (apenas números)"
                                    value={data.did || ""}
                                    onChange={(v) => updateData({ did: v.replace(/\D\+/g, '') })}
                                    error={fieldErrors?.["chave_api.did"]}
                                />
                                {!isDidInboundValid && (
                                    <div>
                                        <small style={{ color: "var(--tc-color-warning-600)" }}>
                                            Não parece estar no formato correto ({data.phoneNumberFormat || 'E164'}).
                                        </small>
                                    </div>
                                )}
                                <div>
                                    <small style={{ color: "var(--tc-color-gray-500)" }}>
                                        Número que o provedor SIP enviará as chamadas (sem código do país)
                                    </small>
                                </div>
                            </GridItem>

                            <GridItem sm={12} md={6}>
                                <CampoMultiTexto
                                    label="IPs Permitidos"
                                    placeholder="Ex: 189.113.38.59, 186.209.45.20"
                                    value={data.allowedIps}
                                    onChange={(v) => updateData({
                                        allowedIps: v ? v.map(ip => ip.trim()).filter(f => f.match(/^(\d{1,3}\.){3}\d{1,3}(?:\/\d{1,2})?$/)).filter(Boolean) : undefined
                                    })}
                                    error={fieldErrors?.["chave_api.allowedIps"]}
                                />
                                <small style={{ color: "var(--tc-color-gray-500)" }}>
                                    IPs do provedor SIP (opcional, para segurança).
                                </small>
                            </GridItem>
                        </Grid>
                    </div>
                </GridItem>
                <GridItem sm={12} md={6}>
                    {/* Seção: Configuração para Ligações Saintes (Outbound) */}
                    <div
                        style={{
                            border: "1px solid var(--tc-color-gray-300)",
                            borderRadius: "8px",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                padding: "12px 16px",
                                backgroundColor: "var(--tc-color-gray-100)",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <h4 style={{ margin: 0 }}>Configuração para Ligações Saintes (Outbound)</h4>
                        </div>

                        <div style={{ padding: "16px" }}>
                            <p style={{ marginBottom: "16px", color: "var(--tc-color-gray-600)" }}>
                                Configure os dados do seu provedor SIP para realizar ligações.
                                <br />
                                <small>Deixe em branco se não deseja realizar chamadas saintes.</small>
                            </p>

                            <Grid>
                                <GridItem sm={12} md={8}>
                                    <CampoTexto
                                        label="Servidor SIP"
                                        placeholder="Ex: sip.provedor.com.br"
                                        value={data.outboundServer || ""}
                                        onChange={(v) => updateData({ outboundServer: v })}
                                        error={fieldErrors?.["chave_api.outboundServer"]}
                                    />
                                </GridItem>

                                <GridItem sm={12} md={4}>
                                    <CampoTexto
                                        label="Porta"
                                        placeholder="5060"
                                        value={data.outboundPort?.toString() || ""}
                                        onChange={(v) => updateData({ outboundPort: v ? parseInt(v) : undefined })}
                                        error={fieldErrors?.["chave_api.outboundPort"]}
                                    />
                                </GridItem>

                                <GridItem sm={12} md={12}>
                                    <CampoTexto
                                        label="Número de Origem (Caller ID)"
                                        placeholder="Ex: +5562999998888 (deixe vazio para usar o DID)"
                                        value={data.outboundFromNumber || ""}
                                        onChange={(v) => updateData({ outboundFromNumber: v.replace(/\D\+/g, '') })}
                                        error={fieldErrors?.["chave_api.outboundFromNumber"]}
                                    />
                                    {!isCallerIdOutboundValid && (
                                        <div>
                                            <small style={{ color: "var(--tc-color-warning-600)" }}>
                                                Não parece estar no formato correto ({data.phoneNumberFormat || 'E164'}).
                                            </small>
                                        </div>
                                    )}
                                    <small style={{ color: "var(--tc-color-gray-500)" }}>
                                        Se não informado, será usado o DID.
                                    </small>
                                </GridItem>

                                <GridItem sm={12}>
                                    <p style={{ marginTop: "16px", color: "var(--tc-color-gray-600)", fontSize: "14px" }}>
                                        <strong>Autenticação</strong> (opcional - deixe em branco se o provedor não exigir usuário/senha)
                                    </p>
                                </GridItem>

                                <GridItem sm={12} md={6}>
                                    <CampoTexto
                                        label="Usuário"
                                        placeholder="Usuário para autenticação SIP (opcional)"
                                        value={data.outboundUsername || ""}
                                        onChange={(v) => updateData({ outboundUsername: v })}
                                        error={fieldErrors?.["chave_api.outboundUsername"]}
                                    />
                                </GridItem>

                                <GridItem sm={12} md={6}>
                                    <CampoSenha
                                        label="Senha"
                                        placeholder="Senha para autenticação SIP (opcional)"
                                        value={data.outboundPassword || ""}
                                        onChange={(v) => updateData({ outboundPassword: v })}
                                        error={fieldErrors?.["chave_api.outboundPassword"]}
                                    />
                                </GridItem>
                            </Grid>
                        </div>
                    </div>
                </GridItem>
            </Grid>

        </VBox>
    );
};
